import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { MavrixPairsConfigurations } from "@/types"
import { useUserAccounts } from "@trader-dashboard/shared";
import { useDemoAwareAccountData } from "@/data/useDemoAccountData";
import { UsdMetrics, BalanceMetrics, ActiveMoneyMetrics, MarginLevelMetrics, OvertimeProfitMetrics } from "./components/MainMetrics"
import { PairsTable, DailyProfitMetrics, OpenTradesMetrics, RecentEventsCard, EventsMetrics, BasketMonitorPanel } from "./components";
import { LoadingView } from '@/components';
import { useFunds } from '@/context/FundsContext';
import { isDemoAccount, DEMO_ACCOUNT_ID } from '@/data/demoAccountHelper';

export const AccountDashboard = ():React.ReactNode => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { accountId } = useParams();

  const {refreshAccountData, loaded: accountDataLoaded, accountData, error:accountLoadError
  } = useDemoAwareAccountData();
  const {selectedAccountId} = useUserAccounts();
  const {fundsStats, loaded: fundsLoaded, refresh: refreshFunds} = useFunds();

  useEffect(() => {
    if (isDemoAccount(accountId)) {
      refreshFunds(DEMO_ACCOUNT_ID);
    }
  }, [accountId, refreshFunds]);

  useEffect(() => {
    if (accountLoadError) {
      setError(accountLoadError);
    }
  }, [accountLoadError]);

  useEffect(() => {
    setError(undefined);
    setLoaded(accountDataLoaded);
  }, [accountDataLoaded]);

  const getPairsView = (): React.ReactNode => {
    if (!accountData) return <div>Loading...</div>;

    return (
      <PairsTable
        pairs={accountData.pairs || {}}
        pairConfigurations={MavrixPairsConfigurations}
        totalProfit={accountData.totalProfit}
        showAllPairs={false}
        accountId={accountId || selectedAccountId}
        maxPairs={5}
        showTypeColumn={false}
      />
    );
  };

  const onRefreshRequested = async () => {
    await refreshAccountData();
  }

  if (error) return <div>Error: {error}</div>;

  if (!loaded || !accountData) return <LoadingView/>;

  const drawDownPercentage = Math.abs(accountData.totalProfit) / accountData.balance * 100;

  const activeMoney = accountData.balance - accountData.freeMargin;
  //const activeMoneyProgress = activeMoney / accountData.balance * 100;

  return (
    <div className="flex flex-col gap-8">
      <div className="grid gap-4 md:grid-cols-2 md:gap-8 lg:grid-cols-3 xl:grid-cols-4">
        <ActiveMoneyMetrics title="Active Money"
          value={activeMoney}
          activeMoneyPercentage={activeMoney / accountData.balance * 100}
          lastUpdatedAt={accountData.lastUpdatedAt}
          onRefreshRequested={onRefreshRequested}
        />
        <OpenTradesMetrics
          openPositions={accountData.openPositions}
          pairs={accountData.pairs || []}
        />
        <EventsMetrics />
        <DailyProfitMetrics />
        <UsdMetrics title="Current Drawdown"
          value={Math.abs(accountData.totalProfit)}
          shouldShowProgress={accountData.totalProfit !== 0}
          progress={drawDownPercentage}
          progressTitle={accountData.totalProfit === 0 ? 'No drawdown' : drawDownPercentage.toFixed(2) + '% from balance'}
        />
        <BalanceMetrics title="Balance" value={accountData.balance} />
        <MarginLevelMetrics
          freeMargin={accountData.freeMargin}
          balance={accountData.balance}
          equity={accountData.equity}
        />
        <OvertimeProfitMetrics
          fundsLoaded={fundsLoaded}
          allTimeProfit={fundsStats.allTimeProfit}
          totalDeposits={fundsStats.totalDeposits}
          totalWithdrawals={fundsStats.totalWithdrawals}
        />
      </div>
      <BasketMonitorPanel />
      <RecentEventsCard accountId={accountId || selectedAccountId} />
      {getPairsView()}
    </div>
  )
}
